import { config } from '@/config/env'

/**
 * Builds the whiteboard URL this client will frame.
 *
 * The room broadcasts only that a board is open and who opened it — never a
 * URL. Every client derives the same board from the room id and then appends
 * its OWN identity and edit rights, which is the point: a participant cannot
 * hand another participant a link to load, and cannot promote themselves to
 * editor by editing what they send.
 *
 * The board key is derived from the room id rather than transmitted, so both
 * platforms compute it independently. **This mirrors the Dart implementation in
 * `lib/features/meeting/.../whiteboard_url.dart` exactly — SHA-256 of the room
 * id, first 16 bytes, base64url, unpadded. Change one and you must change the
 * other, or web and mobile land in different boards for the same meeting.**
 *
 * Note on secrecy: anyone who knows the room id can derive the key. That is
 * acceptable because the room id is already the thing that gates the meeting —
 * but it is a derivation, not a secret, and should not be treated as one. (The
 * reference implementation was weaker still: one constant key for every meeting
 * it ever hosted.)
 */

/** Excalidraw expects a 22-char unpadded base64url key (128 bits). */
const KEY_BYTES = 16

function base64UrlUnpadded(bytes: Uint8Array): string {
  let binary = ''
  for (const b of bytes) binary += String.fromCharCode(b)
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

/**
 * Deterministic board key for a room.
 *
 * Async because it uses WebCrypto, which needs a secure context — fine for the
 * app (https, or localhost in dev) but it does mean callers await once and
 * cache, rather than recomputing per render.
 */
export async function deriveBoardKey(roomId: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(roomId))
  return base64UrlUnpadded(new Uint8Array(digest).slice(0, KEY_BYTES))
}

export type WhiteboardIdentity = {
  roomId: string
  /** Shown to other participants inside the board. */
  displayName: string
  /** False → the board loads read-only for this participant. */
  canEdit: boolean
}

/**
 * `<base>#room=<roomId>,<key>,<name>,<readOnly>`
 *
 * The trailing two params match the self-hosted board's fork (see the reference
 * client, which appends first name and a read-only flag the same way). Only the
 * first name is passed — the board renders it as a cursor label, and full names
 * overflow it.
 */
export function buildWhiteboardUrl(key: string, identity: WhiteboardIdentity): string {
  const base = config.whiteboardUrl
  if (!base) return ''

  const firstName = (identity.displayName || '').trim().split(/\s+/)[0] || 'Guest'
  const readOnly = identity.canEdit ? 'false' : 'true'

  // The base already ends in the board's path; the fragment carries everything
  // else, which is what keeps the room key out of server logs and Referer.
  return `${base}#room=${identity.roomId},${key},${encodeURIComponent(firstName)},${readOnly}`
}

/** Whether the feature is usable at all in this deployment. */
export function whiteboardConfigured(): boolean {
  return !!config.whiteboardUrl
}
